// Shared formatting/coloring helpers for the "At a glance" sub-tabs (ConcessionGlance,
// DaysOnMarketGlance, DelinquencyGlance, VacancyGlance, ExposureGlance) — ported from LC2's
// tableColumns.js (`getRandomValue`, the crypto-based row id generator used wherever the raw
// glance rows don't carry their own unique id) and TableBodyCell.js (commons/components/
// @extended/TableBodyCell.js) `dataFormat`/`color` handling, scoped down to just the formats
// the Glance columns actually use:
//   - 'currency'   -> "$"-prefixed, comma-grouped, 2 decimals
//   - 'percentage' -> "X.X%" for numbers, "X%" for pre-formatted strings, 'N/A' passes through
//   - 'numeric'    -> comma-grouped number
//   - 'date'       -> "MMM D, YYYY" via dayjs
//   - 'lease_days' -> ExposureGlance's `until_lease_end` special case ("1 day" vs "X days")
//   - default      -> value as-is
// `color: 'red/green'` mirrors LC2 exactly: with a `colorValue` threshold, values >= the
// threshold are colored error (red) and below it success (green) — used for "days on market"
// columns where a higher DOM is bad; without a threshold, negative values are error and
// positive values are success — used for rent/reduction deltas where the sign itself is the
// signal.
import dayjs from 'dayjs';

export const getRandomValue = () => {
    const array = new Uint32Array(1);
    window.crypto.getRandomValues(array);
    return array[0];
};

export const formatGlanceValue = (value, format) => {
    if (value === null || value === undefined || value === 'null' || value === '') return '';

    switch (format) {
        case 'currency':
            return typeof value === 'number'
                ? `$${value.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
                : value;
        case 'percentage':
            if (value === 'N/A') return value;
            return typeof value === 'number' ? `${value.toFixed(1)}%` : `${value}%`;
        case 'numeric':
            return typeof value === 'number' ? value.toLocaleString() : value;
        case 'date':
            return dayjs(value).format('MMM D, YYYY');
        case 'lease_days':
            return value === 1 ? '1 day' : `${value} days`;
        default:
            return value;
    }
};

export const getGlanceValueColor = (value, color, colorValue, theme) => {
    if (color !== 'red/green') return undefined;

    if (colorValue !== undefined && colorValue !== null) {
        return value >= colorValue ? theme.palette.error.main : theme.palette.success.main;
    }
    if (value > 0) return theme.palette.success.main;
    if (value < 0) return theme.palette.error.main;
    return theme.palette.primary.main;
};
